<script setup>
import { computed } from "vue";
import { ExclamationTriangleIcon } from "@heroicons/vue/24/outline";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

/**
 * @typedef {Object} AgentWarning
 * @property {string} message - The warning message to display
 * @property {string} [linkUrl] - Optional documentation URL
 * @property {string} [linkLabel] - Optional aria-label for the link
 */

const props = defineProps({
  agent: {
    type: Object,
    required: true,
  },
  /**
   * Function that returns an array of warnings for the agent.
   * @type {(agent: Object) => AgentWarning[]}
   */
  getWarnings: {
    type: Function,
    default: () => [],
  },
});

const warnings = computed(() => {
  if (typeof props.getWarnings === "function") {
    return props.getWarnings(props.agent);
  }
  return [];
});
</script>

<template>
  <div
    data-testid="agent-card"
    class="flex flex-col space-y-6 rounded-lg border bg-background p-6 shadow-sm transition-colors"
  >
    <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
      {{ t("pages.wizard.steps.agent.heading") }}
    </h3>

    <div class="grid grid-cols-2 gap-6">
      <div>
        <h4 class="text-sm font-medium text-muted-foreground">
          {{ t("pages.wizard.steps.agent.table.columns.Name") }}
        </h4>
        <p class="truncate">
          {{ agent.Name }}
        </p>
      </div>

      <div>
        <h4 class="text-sm font-medium text-muted-foreground">
          {{ t("pages.wizard.steps.agent.table.columns.Info") }}
        </h4>
        <p class="truncate">
          <span v-if="agent.Capabilities['os-release']">
            {{ agent.Capabilities["os-release"].NAME }}
            {{ agent.Capabilities["os-release"].VERSION }}
          </span>
          <span v-else>Unknown</span>
        </p>
      </div>

      <div
        v-for="(warning, index) in warnings"
        :key="index"
        class="border-warning/50 text-warning dark:border-warning [&>svg]:text-warning relative col-span-2 w-full rounded-lg border p-4 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg~*]:pl-7"
        role="alert"
      >
        <ExclamationTriangleIcon class="h-4 w-4" />
        <p class="text-sm [&_p]:leading-relaxed">
          {{ warning.message }}
          <template v-if="warning.linkUrl">
            <br />
            <a
              class="underline hover:no-underline"
              :href="warning.linkUrl"
              target="_blank"
              :aria-label="warning.linkLabel || 'Documentation (opens in new tab)'"
            >
              {{ t("common.documentation").toLowerCase() }}
            </a>
          </template>
        </p>
      </div>
    </div>
  </div>
</template>
